/**
 * FirebaseService — Connects to the existing CDR Firebase/Firestore backend.
 * Uses the same project (cdr-konfigurator) and Firestore database
 * (cdr-configurator-zurich-db) as the original CDR configurator.
 * Also handles Firebase Authentication (email/password).
 */
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, doc, setDoc, getDoc, getDocs, query, where, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import {
  getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword,
  signOut, onAuthStateChanged as firebaseOnAuthStateChanged,
  updateProfile, updatePassword,
  EmailAuthProvider, reauthenticateWithCredential
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: 'AIzaSyAu4ofPvettguVjdIbn5uZ0zN8fs5FuZmQ',
  authDomain: 'cdr-konfigurator.firebaseapp.com',
  projectId: 'cdr-konfigurator',
  storageBucket: 'cdr-konfigurator.appspot.com',
  messagingSenderId: '988586595205',
  appId: '1:988586595205:web:d2f4b68d7e18c54f71eaec',
};

const FIRESTORE_DB = 'cdr-configurator-zurich-db';

export default class FirebaseService {
  constructor() {
    this.app = null;
    this.db = null;
    this._init();
  }

  _init() {
    try {
      this.app = initializeApp(firebaseConfig);
      this.db = getFirestore(this.app, FIRESTORE_DB);
      this.auth = getAuth(this.app);
      console.log('Firebase connected to cdr-konfigurator');
    } catch (e) {
      console.error('Firebase init failed:', e);
    }
  }

  // ── Auth methods ──────────────────────────────────────────

  async loginWithEmail(email, password) {
    const cred = await signInWithEmailAndPassword(this.auth, email, password);
    const profile = await this._getUserProfile(cred.user.uid);
    return { user: cred.user, profile };
  }

  async registerWithEmail(email, password, name, company) {
    const cred = await createUserWithEmailAndPassword(this.auth, email, password);
    await updateProfile(cred.user, { displayName: name });
    // Store company (+ name) in Firestore since Firebase Auth doesn't have a company field
    await setDoc(doc(this.db, 'users', cred.user.uid), {
      name, company, email, createdAt: serverTimestamp()
    });
    return { user: cred.user, profile: { name, company } };
  }

  async logout() {
    await signOut(this.auth);
  }

  onAuthStateChanged(callback) {
    return firebaseOnAuthStateChanged(this.auth, async (user) => {
      if (user) {
        const profile = await this._getUserProfile(user.uid);
        callback(user, profile);
      } else {
        callback(null, null);
      }
    });
  }

  getCurrentUser() {
    return this.auth.currentUser;
  }

  async updateUserProfile(name, company) {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not logged in');
    await updateProfile(user, { displayName: name });
    await setDoc(doc(this.db, 'users', user.uid), {
      name, company, email: user.email, updatedAt: serverTimestamp()
    }, { merge: true });
  }

  async changePassword(currentPassword, newPassword) {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not logged in');
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  }

  async _getUserProfile(uid) {
    try {
      const snap = await getDoc(doc(this.db, 'users', uid));
      return snap.exists() ? snap.data() : {};
    } catch (e) {
      console.warn('Could not load user profile:', e);
      return {};
    }
  }

  /**
   * Generate a unique configuration code (same pattern as original CDR).
   * Format: CDR-XXXXXX (6 alphanumeric chars)
   */
  _generateCode() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // No I,O,0,1 to avoid confusion
    let code = 'CDR-';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }

  /**
   * Submit a configuration/order to Firestore.
   *
   * @param {Object} params
   * @param {Array} params.positions - Array of position objects from OrderManager
   * @param {Object} params.user - Auth user (email, name, company)
   * @param {Object} params.formData - Customer form data (kommission, notiz, dates)
   * @returns {Object} { code, success }
   */
  async submitOrder({ positions, user, formData }) {
    if (!this.db) throw new Error('Firebase not initialized');

    const code = this._generateCode();

    // Build Firestore document matching original CDR structure
    const configDoc = {
      code,
      type: 'requests',
      status: 'new',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),

      // User info
      userEmail: user.email,
      userName: user.name || '',
      userCompany: user.company || '',

      // Form fields
      kommission: formData.kommission || '',
      notiz: formData.notiz || '',
      offerteBis: formData.offerteBis || '',
      lieferungBis: formData.lieferungBis || '',

      // Positions
      positions: positions.map((pos, idx) => ({
        nr: idx + 1,
        frgName: pos.frgName || '—',
        mgName: pos.mgName || '—',
        ogName: pos.ogName || '—',
        structure: pos.structure || '—',
        manufacturer: pos.manufacturer || '—',
        class: pos.class || '—',
        insulation: pos.insulation || '—',
        application: pos.application || '—',
        whiteGlass: pos.whiteGlass || 'Nein',
        resistance: pos.resistance || '—',
        buttJoints: pos.buttJoints || 'Nein',
        weightPerSqm: pos.weightPerSqm || 0,
        variants: (pos.variants || []).map(v => ({
          quantity: parseInt(v.quantity) || 1,
          width: parseFloat(v.width) || 0,
          height: parseFloat(v.height) || 0,
        })),
      })),

      // Totals
      totalSqm: positions.reduce((sum, pos) => {
        return sum + (pos.variants || []).reduce((s, v) => {
          const sqm = (parseFloat(v.width) || 0) * (parseFloat(v.height) || 0) / 1000000;
          return s + sqm * (parseInt(v.quantity) || 1);
        }, 0);
      }, 0),

      // Source
      source: 'configurator-v2',
    };

    // Write to Firestore
    const ref = doc(collection(this.db, 'configurations'), code);
    await setDoc(ref, configDoc);

    console.log(`Order ${code} submitted to Firestore`);
    return { code, success: true };
  }

  /**
   * Fetch order history for a user.
   * @param {string} userEmail
   * @param {number} maxResults
   * @returns {Array} Array of order objects
   */
  async getUserOrders(userEmail, maxResults = 50) {
    if (!this.db) return [];

    try {
      const q = query(
        collection(this.db, 'configurations'),
        where('userEmail', '==', userEmail),
        orderBy('createdAt', 'desc'),
        limit(maxResults)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (e) {
      console.error('Failed to fetch orders:', e);
      return [];
    }
  }
}
